// ─────────────────────────────────────────────────────────────────────────
// Découpe le texte d'un email (demande initiale d'un ticket) en messages :
// la réponse la plus récente en haut, puis les messages cités en dessous.
//
// Formats reconnus :
//   • Outlook :      De : … / Envoyé : … / À : … / Cc : … / Objet : …   (+ From/Sent/To/Subject)
//   • Transferts :   « -------- Message transféré -------- », « Forwarded message »,
//                    « Message d'origine », « Début du message réexpédié : »…
//                    (libellé et valeur sur la même ligne ou sur des lignes séparées)
//   • Gmail :        « Le mar. 13 oct. 2026 à 15:38, X <x@y> a écrit : » + lignes citées « > »
//
// Rien n'est perdu : tout le texte reste dans un message ; en cas de doute la
// ligne reste dans le corps. Nettoyage : lignes « [image] », liens
// « [texte](https://…) » → « texte », lignes vides en trop.
// ─────────────────────────────────────────────────────────────────────────

// Domaines « maison » : leurs messages s'affichent de notre côté (à droite).
export const INTERNAL_DOMAINS = ["accen.fr"];

const norm = (s) =>
    (s === undefined || s === null ? "" : String(s))
        .normalize("NFD")
        .replace(/[̀-ͯ]/g, "")
        .toLowerCase()
        .replace(/\s+/g, " ")
        .trim();

const LABEL_KEYS = {
    de: "from", from: "from", expediteur: "from",
    envoye: "date", "envoye le": "date", sent: "date", date: "date",
    a: "to", to: "to", pour: "to", destinataire: "to", destinataires: "to",
    cc: "cc", cci: "cc", bcc: "cc",
    objet: "subject", subject: "subject", sujet: "subject",
};
const LABEL_RE = /^\s*[*_]{0,2}\s*([A-Za-zÀ-ɏ][A-Za-zÀ-ɏ' ]{0,20}?)\s*[*_]{0,2}\s*:\s*[*_]{0,2}\s*(.*?)\s*$/;
const SEPARATOR_RE = /^-*\s*(message transfere|forwarded message|original message|message d'origine|message original|debut du message reexpedie|begin forwarded message)\s*:?\s*-*$/;
const UNDERSCORES_RE = /^\s*_{8,}\s*$/;
const WROTE_RE = /^\s*(?:le|on)\s+(.+?)\s*(?:a ecrit|wrote)\s*:\s*$/;
const CLOSING_RE = /^(bien )?(cordialement|cdlt|a bientot|bien a (vous|toi)|bonne (journee|soiree|fin de journee|apres-midi|semaine|continuation)|(sinceres )?salutations( distinguees)?|merci( beaucoup| d'avance| par avance| encore)?|best regards|kind regards|regards|thanks|thank you)[\s,.!]*$/;

function matchLabel(line) {
    const m = LABEL_RE.exec(line || "");
    if (!m) return null;
    const key = LABEL_KEYS[norm(m[1])];
    if (!key) return null;
    return { key, value: m[2].replace(/^[*_]+|[*_]+$/g, "").trim() };
}

const nextFilled = (lines, j) => {
    while (j < lines.length && !lines[j].trim()) j++;
    return j;
};

// Lit un bloc d'en-tête (libellés « De : », « Objet : »…) à partir de la ligne start.
function parseFields(lines, start) {
    const fields = {};
    let count = 0;
    let lastKey = null;
    let j = start;
    while (j < lines.length) {
        const line = lines[j];
        if (!line.trim()) { j++; continue; }
        const lab = matchLabel(line);
        if (lab) {
            lastKey = lab.key;
            if (lab.value) {
                if (!fields[lab.key]) { fields[lab.key] = lab.value; count++; }
                j++;
            } else {
                // valeur sur la ligne suivante (format Thunderbird)
                const k = nextFilled(lines, j + 1);
                if (k < lines.length && !matchLabel(lines[k]) && !SEPARATOR_RE.test(norm(lines[k]))) {
                    if (!fields[lab.key]) { fields[lab.key] = lines[k].trim(); count++; }
                    j = k + 1;
                } else {
                    j = k;
                }
            }
            continue;
        }
        // Valeur longue sur plusieurs lignes (ex. liste « À : ») tant qu'un libellé suit.
        if (lastKey && lastKey !== "subject" && !fields.subject) {
            const k = nextFilled(lines, j + 1);
            if (k < lines.length && matchLabel(lines[k])) {
                fields[lastKey] = (fields[lastKey] ? fields[lastKey] + " " : "") + line.trim();
                j++;
                continue;
            }
        }
        break; // début du corps du message
    }
    return { fields, count, next: j };
}

// « Le mar. 13 oct. 2026 à 15:38, Support Accen <support@accen.fr> a écrit : »
function parseWrote(text) {
    const m = WROTE_RE.exec(norm(text));
    // Il faut une adresse ou une heure (évite « Le technicien a écrit : » dans un texte).
    if (!m || !/@|\d{1,2}[:h]\d{2}/.test(m[1])) return null;
    // On repart du texte d'origine (accents, casse) pour l'affichage.
    const raw = text.replace(/\s+/g, " ").trim()
        .replace(/^(le|on)\s+/i, "")
        .replace(/\s*(a [ée]crit|wrote)\s*:\s*$/i, "");
    let date = "";
    let from = raw;
    const t = raw.match(/^(.*?\d{1,2}[:h]\d{2}(?:\s*[AP]M)?)\s*,?\s+(.+)$/i);
    if (t) {
        date = t[1].replace(/,\s*$/, "").trim();
        from = t[2].trim();
    } else {
        const c = raw.lastIndexOf(", ");
        if (c !== -1) { date = raw.slice(0, c).trim(); from = raw.slice(c + 2).trim(); }
    }
    return { from, date };
}

// Début d'un message cité à la ligne i ? → { header, next, quoted }
function detectHeader(lines, i) {
    const line = lines[i];
    const n = norm(line);
    if (!n) return null;

    // Ligne de soulignés Outlook juste avant « De : … »
    if (UNDERSCORES_RE.test(line)) {
        const k = nextFilled(lines, i + 1);
        const h = k < lines.length ? detectHeader(lines, k) : null;
        return h && !h.quoted ? h : null;
    }

    // « -------- Message transféré -------- » & co
    if (SEPARATOR_RE.test(n) && /[a-z]/.test(n)) {
        const p = parseFields(lines, i + 1);
        return { header: Object.assign({ forwarded: true }, p.fields), next: p.count ? p.next : i + 1 };
    }

    // « Le …, X <x@y> a écrit : » (éventuellement coupé sur 2-3 lignes)
    if (/^(le|on)\s/.test(n)) {
        let joined = line;
        for (let k = 0; k < 3 && i + k < lines.length; k++) {
            if (k > 0) joined += " " + lines[i + k];
            const w = parseWrote(joined);
            if (w) return { header: { from: w.from, date: w.date }, next: i + k + 1, quoted: true };
        }
    }

    // Bloc Outlook : « De : … » suivi d'au moins une autre information.
    const lab = matchLabel(line);
    if (lab && lab.key === "from") {
        const p = parseFields(lines, i);
        const f = p.fields;
        if (f.from && (f.date || f.to || f.subject) && p.count >= 2) {
            return { header: f, next: p.next };
        }
    }
    return null;
}

// Dates d'en-tête très variées → « 29/09/2026 10:33 » (texte d'origine sinon).
const MONTHS = {
    janvier: 1, janv: 1, jan: 1, january: 1, fevrier: 2, fevr: 2, fev: 2, feb: 2, february: 2,
    mars: 3, mar: 3, march: 3, avril: 4, avr: 4, apr: 4, april: 4, mai: 5, may: 5,
    juin: 6, jun: 6, june: 6, juillet: 7, juil: 7, jul: 7, july: 7, aout: 8, aug: 8, august: 8,
    septembre: 9, sept: 9, sep: 9, september: 9, octobre: 10, oct: 10, october: 10,
    novembre: 11, nov: 11, november: 11, decembre: 12, dec: 12, december: 12,
};
export function compactDate(value) {
    const s = norm(value);
    if (!s) return "";
    const pad = (n) => (n < 10 ? "0" : "") + n;
    let d;
    let mo;
    let y;
    const num = s.match(/\b(\d{1,2})[/.-](\d{1,2})[/.-](\d{2,4})\b/);
    if (num) {
        d = +num[1];
        mo = +num[2];
        y = +num[3] < 100 ? +num[3] + 2000 : +num[3];
    } else {
        const ym = s.match(/\b(19|20)\d{2}\b/);
        const names = (s.match(/[a-z]+/g) || []).filter((w) => MONTHS[w]);
        if (!ym || !names.length) return value;
        y = +ym[0];
        // Jour avant le mois (« 13 oct. ») d'abord : « mar. 13 oct. » = mardi, pas mars.
        let found = null;
        names.some((w) => {
            const m1 = s.match(new RegExp("\\b(\\d{1,2})(?:er)?\\.?\\s+" + w + "\\b"));
            if (m1) found = { d: +m1[1], mo: MONTHS[w] };
            return !!found;
        });
        if (!found) {
            names.some((w) => {
                const m2 = s.match(new RegExp("\\b" + w + "\\.?\\s+(\\d{1,2})\\b"));
                if (m2) found = { d: +m2[1], mo: MONTHS[w] };
                return !!found;
            });
        }
        if (!found) return value;
        d = found.d;
        mo = found.mo;
    }
    if (!(d >= 1 && d <= 31 && mo >= 1 && mo <= 12)) return value;
    let out = pad(d) + "/" + pad(mo) + "/" + y;
    const t = s.match(/\b(\d{1,2})[:h](\d{2})(?::\d{2})?\s*(am|pm)?\b/);
    if (t) {
        let hh = +t[1];
        if (t[3] === "pm" && hh < 12) hh += 12;
        if (t[3] === "am" && hh === 12) hh = 0;
        if (hh <= 23) out += " " + pad(hh) + ":" + t[2];
    }
    return out;
}

// « Nom <adresse> », « 'Nom' <adresse> », « adresse », « Nom [mailto:adresse] »
export function parseAddress(value) {
    const s = (value || "").trim();
    const m = s.match(/^(.*?)\s*[<[](?:mailto:)?([^\s<>[\]]+@[^\s<>[\]]+)[>\]]/i);
    if (m) return { name: m[1].replace(/^["'\s]+|["'\s]+$/g, "").trim(), email: m[2] };
    const e = s.match(/[^\s<>"',;:]+@[^\s<>"',;]+/);
    if (e) {
        const name = s.replace(e[0], "").replace(/^["'\s<>]+|["'\s<>]+$/g, "").trim();
        return { name: name === e[0] ? "" : name, email: e[0] };
    }
    return { name: s.replace(/^["']+|["']+$/g, ""), email: "" };
}

// « 'Support Accen' <support@accen.fr>; <x@y.fr> » → « Support Accen <support@accen.fr>; x@y.fr »
const cleanAddrList = (s) =>
    String(s || "")
        .replace(/(^|[;,]\s*)["']([^"';,]+)["']/g, "$1$2")
        .replace(/(^|[;,]\s*)<([^<>\s]+@[^<>\s]+)>/g, "$1$2")
        .trim();

const isInternal = (email) => {
    const d = norm(email).split("@")[1] || "";
    return !!d && INTERNAL_DOMAINS.some((x) => d === x || d.slice(-(x.length + 1)) === "." + x);
};

function trimBlank(lines) {
    let a = 0;
    let b = lines.length;
    while (a < b && !lines[a].trim()) a++;
    while (b > a && !lines[b - 1].trim()) b--;
    const out = [];
    lines.slice(a, b).forEach((l) => {
        if (!l.trim() && out.length && !out[out.length - 1].trim()) return; // un seul blanc d'affilée
        out.push(l);
    });
    return out;
}

function cleanLines(lines) {
    return trimBlank(
        lines
            .filter((l) => !/^\s*\[(image|cid:[^\]]*)\]\s*$/i.test(l) && !UNDERSCORES_RE.test(l))
            .map((l) => l.replace(/\[([^\]\n]+)\]\((?:https?:\/\/|mailto:)[^)\s]*\)/g, "$1").replace(/\s+$/, ""))
    );
}

// Signature = ce qui suit la dernière formule de politesse (si c'est court).
function splitSignature(lines) {
    for (let i = lines.length - 1; i >= 0; i--) {
        if (CLOSING_RE.test(norm(lines[i]))) {
            const rest = trimBlank(lines.slice(i + 1));
            const filled = rest.filter((l) => l.trim()).length;
            if (!filled || filled > 15) break;
            return { body: trimBlank(lines.slice(0, i + 1)), signature: rest };
        }
    }
    return { body: lines, signature: [] };
}

/**
 * @param {string} text   texte de l'email (demande initiale)
 * @param {object} opts   { sender: "Nom <adresse>" ou adresse, date: date du dernier message }
 * @returns {{ hasHeaders: boolean, messages: Array }} messages du plus ancien au plus récent
 */
export function parseMailThread(text, opts) {
    const o = opts || {};
    const lines = String(text || "").replace(/\r\n?/g, "\n").split("\n");
    const segments = [];
    let cur = { header: null, lines: [] };
    let i = 0;
    while (i < lines.length) {
        const h = detectHeader(lines, i);
        if (h) {
            segments.push(cur);
            cur = { header: h.header, lines: [] };
            if (h.quoted) {
                // Réponse Gmail : on retire un niveau de citation « > » à la suite.
                for (let k = h.next; k < lines.length; k++) lines[k] = lines[k].replace(/^ ?> ?/, "");
            }
            i = h.next;
            continue;
        }
        cur.lines.push(lines[i]);
        i++;
    }
    segments.push(cur);

    const hasHeaders = segments.some((s) => s.header);
    const messages = [];
    segments.forEach((s, idx) => {
        const parts = splitSignature(cleanLines(s.lines));
        const h = s.header || {};
        if (!s.header && !parts.body.length && !parts.signature.length) return; // vide (ex. transfert sans texte)
        let who = parseAddress(h.from || "");
        if (idx === 0 && !s.header) {
            // Message le plus récent : expéditeur du ticket, sinon le destinataire du message cité.
            who = parseAddress(o.sender || "");
            if (!who.email && !who.name && segments[1] && segments[1].header && segments[1].header.to) {
                who = parseAddress(String(segments[1].header.to).split(/[;,]/)[0]);
            }
        }
        const date = s.header ? (h.date || "") : (o.date || "");
        messages.push({
            name: who.name,
            email: who.email,
            date: date,
            dateLabel: s.header ? compactDate(date) : date,
            to: cleanAddrList(h.to),
            cc: cleanAddrList(h.cc),
            subject: h.subject || "",
            forwarded: !!h.forwarded,
            internal: isInternal(who.email),
            body: parts.body.join("\n"),
            signature: parts.signature.join("\n"),
        });
    });
    return { hasHeaders, messages: messages.reverse() };
}

// Couleur d'avatar stable par correspondant (ACCEN en indigo) — partagée par
// le détail du ticket et l'aperçu au survol.
const MAIL_COLORS = ["#26A69A", "#EF6C00", "#AB47BC", "#0288D1", "#8D6E63", "#D81B60", "#43A047", "#546E7A"];
export function mailColor(m) {
    if (m.internal) return "#5C6BC0";
    const key = norm(m.email || m.name);
    let h = 0;
    for (let i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) >>> 0;
    return MAIL_COLORS[h % MAIL_COLORS.length];
}
